import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import QuizProgress from './QuizProgress';
import QuizStep from './QuizStep';
import ResultsDisplay from './ResultsDisplay';
import { useQuizState } from '../../hooks/useQuizState';
import { useQuizResults } from '../../hooks/useLocalStorage';
import { calculateDoshaScores, getDoshaBalance } from '../../utils/doshaCalculator';
import i18n from '../../i18n';

const DoshaQuiz = () => {
  const [currentView, setCurrentView] = useState('intro'); // 'intro', 'quiz', 'results'
  const [quizResults, setQuizResults] = useState(null);
  const { saveResult, getLatestResult, clearResults } = useQuizResults();
  const quizContainerRef = useRef(null);

  const {
    currentStep,
    totalSteps,
    categories,
    getCurrentCategoryData,
    getAnswersForCategory,
    getCurrentCategory,
    setAnswer,
    nextStep,
    previousStep,
    canProceedToNext,
    canGoToPrevious,
    isLastStep,
    isQuizComplete,
    getAllAnswers,
    resetQuiz,
    hasProgress,
    clearProgress,
    getCompletedSteps
  } = useQuizState();

  // Check for existing progress or completed results on mount
  useEffect(() => {
    const latestResult = getLatestResult();
    
    if (latestResult) {
      // If there's a completed result, show it
      setQuizResults(latestResult);
      setCurrentView('results');
    } else if (hasProgress && currentView === 'intro') {
      // If there's progress but no completed result, continue quiz
      setCurrentView('quiz');
    }
  }, []); // Only run on mount to avoid infinite loops

  const handleStartQuiz = () => {
    setCurrentView('quiz');
    // Scroll to quiz after state update
    setTimeout(scrollToQuizTop, 100);
  };

  const handleResumeQuiz = () => {
    setCurrentView('quiz');
    // Scroll to quiz after state update
    setTimeout(scrollToQuizTop, 100);
  };

  const handleStartOver = () => {
    resetQuiz();
    clearResults();
    setCurrentView('quiz');
    setQuizResults(null);
    // Scroll to quiz after state update
    setTimeout(scrollToQuizTop, 100);
  };

  const scrollToQuizTop = () => {
    if (quizContainerRef.current) {
      quizContainerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    const category = getCurrentCategory();
    if (category) {
      setAnswer(category, questionId, answer);
    }
  };

  const handleNext = () => {
    if (isLastStep() && isQuizComplete()) {
      // Calculate results
      const answers = getAllAnswers();
      const scores = calculateDoshaScores(answers);
      const results = getDoshaBalance(scores);
      
      // Automatically save results when quiz is completed
      const savedResult = saveResult(results);
      
      setQuizResults(savedResult);
      setCurrentView('results');
    } else if (canProceedToNext()) {
      nextStep();
      // Scroll to top of quiz container after step change
      setTimeout(scrollToQuizTop, 100);
    }
  };

  const handlePrevious = () => {
    if (canGoToPrevious()) {
      previousStep();
      // Scroll to top of quiz container after step change
      setTimeout(scrollToQuizTop, 100);
    }
  };

  const handleStepClick = (stepIndex) => {
    // Allow navigation to completed steps or current step
    if (stepIndex <= getCompletedSteps() || stepIndex === currentStep) {
      // This would need to be implemented in useQuizState
      // setCurrentStep(stepIndex);
      // Scroll to top when step is clicked
      setTimeout(scrollToQuizTop, 100);
    }
  };


  if (currentView === 'results' && quizResults) {
    return (
      <ResultsDisplay
        results={quizResults}
        onStartOver={handleStartOver}
      />
    );
  }

  if (currentView === 'quiz') {
    return (
      <div className="min-h-screen bg-warm-white">
        {/* Header */}
        <section className="bg-gradient-earth pt-24 pb-8">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="font-display text-white text-4xl md:text-5xl font-bold mb-4">
              Discover Your Dosha
            </h1>
            <p className="text-blue-100 text-lg">
              Answer these questions to understand your unique Ayurvedic constitution
            </p>
          </div>
        </section>

        {/* Quiz Content */}
        <div ref={quizContainerRef} className="max-w-4xl mx-auto px-4 py-8">
          {/* Progress Indicator */}
          <QuizProgress
            currentStep={currentStep}
            totalSteps={totalSteps}
            categories={categories}
            completedSteps={getCompletedSteps()}
            onStepClick={handleStepClick}
          />

          {/* Quiz Step */}
          <QuizStep
            categoryData={getCurrentCategoryData()}
            answers={getAnswersForCategory(getCurrentCategory())}
            onAnswerChange={handleAnswerChange}
            currentStep={currentStep}
            totalSteps={totalSteps}
          />

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center mt-12 pt-8 border-t border-gray-200">
            <div>
              {canGoToPrevious() ? (
                <button
                  onClick={handlePrevious}
                  className="btn-secondary px-6 py-3 rounded-lg font-medium inline-flex items-center space-x-2"
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Previous</span>
                </button>
              ) : (
                <Link
                  to="/"
                  className="btn-secondary px-6 py-3 rounded-lg font-medium"
                >
                  Back
                </Link>
              )}
            </div>

            <div className="text-center">
              <button
                onClick={handleStartOver}
                className="text-gray-500 hover:text-gray-700 text-sm font-medium"
              >
                Start Over
              </button>
            </div>

            <div>
              {canProceedToNext() ? (
                <button
                  onClick={handleNext}
                  className="btn-primary px-6 py-3 rounded-lg font-medium flex items-center space-x-2"
                >
                  <span>
                    {isLastStep() && isQuizComplete() ? 'View Results' : 'Next'}
                  </span>
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              ) : (
                <button
                  disabled
                  className="btn-secondary opacity-50 cursor-not-allowed px-6 py-3 rounded-lg font-medium"
                >
                  Unfinished
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Intro view
  return (
    <div className="min-h-screen bg-warm-white">
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 bg-gradient-earth overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-10 w-32 h-32 bg-white rounded-full"></div>
          <div className="absolute bottom-20 left-10 w-24 h-24 bg-white rounded-full"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <div className="animate-fade-in-up">
            <span className="text-green-200 font-body font-semibold text-lg tracking-wide uppercase block mb-6">
              Personalized Assessment
            </span>
            <h1 className="font-display text-white text-5xl md:text-6xl lg:text-7xl font-bold mb-8">
              Discover Your Dosha
            </h1>
            <div className="mx-auto space-y-3">
              <p className="text-blue-100 text-xl md:text-2xl font-light">
                Take our comprehensive Ayurvedic assessment to understand your unique constitution
              </p>
              <div className="text-green-200 text-xl md:text-2xl font-medium">
                Get personalized recommendations for treatments, diet, and lifestyle
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          {/* What You'll Discover */}
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl font-bold text-primary-dark mb-8">
              What You'll Discover
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="card-wellness p-6 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-primary-dark mb-2">Your Constitution</h3>
                <p className="text-gray-600 text-sm">
                  Understand your unique blend of Vata, Pitta, and Kapha doshas
                </p>
              </div>

              <div className="card-wellness p-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-secondary" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-primary-dark mb-2">Personalized Recommendations</h3>
                <p className="text-gray-600 text-sm">
                  Get tailored advice for diet, lifestyle, and healing treatments
                </p>
              </div>

              <div className="card-wellness p-6 text-center">
                <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-accent" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="font-semibold text-primary-dark mb-2">Treatment Matching</h3>
                <p className="text-gray-600 text-sm">
                  Discover which Ayurvedic treatments are best suited for your dosha
                </p>
              </div>
            </div>
          </div>

          {/* Quiz Details */}
          <div className="card-wellness p-8 mb-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="font-display text-2xl font-bold text-primary-dark mb-4">
                  Comprehensive Assessment
                </h3>
                <div className="space-y-4 text-gray-600">
                  <div className="flex items-start space-x-3">
                    <span className="text-secondary font-bold">1.</span>
                    <span><strong>Physical Characteristics:</strong> Body type, skin, hair, and constitution</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-secondary font-bold">2.</span>
                    <span><strong>Mental & Emotional:</strong> Personality, stress response, and learning style</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-secondary font-bold">3.</span>
                    <span><strong>Lifestyle Patterns:</strong> Sleep, exercise, and daily routine preferences</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-secondary font-bold">4.</span>
                    <span><strong>Digestive Health:</strong> Appetite, digestion strength, and food preferences</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-secondary font-bold">5.</span>
                    <span><strong>Health Tendencies:</strong> Energy levels, common issues, and seasonal patterns</span>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <div className="bg-blue-50 p-6 rounded-xl mb-6">
                  <div className="text-4xl font-bold text-primary mb-2">~15</div>
                  <div className="text-gray-600">minutes to complete</div>
                </div>
                <div className="text-sm text-gray-500">
                  Your progress is automatically saved so you can resume anytime
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center">
            {getLatestResult() ? (
              <div className="space-y-4">
                <p className="text-gray-600 mb-6">
                  You have completed the dosha assessment. View your results or take it again.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => {
                      setQuizResults(getLatestResult());
                      setCurrentView('results');
                    }}
                    className="btn-primary text-lg px-8 py-4 rounded-full font-display"
                  >
                    View My Results
                  </button>
                  <button
                    onClick={handleStartOver}
                    className="btn-secondary text-lg px-8 py-4 rounded-full font-display"
                  >
                    Retake Assessment
                  </button>
                </div>
              </div>
            ) : hasProgress ? (
              <div className="space-y-4">
                <p className="text-gray-600 mb-6">
                  You have a quiz in progress. Would you like to continue where you left off?
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={handleResumeQuiz}
                    className="btn-primary text-lg px-8 py-4 rounded-full font-display"
                  >
                    Resume Quiz
                  </button>
                  <button
                    onClick={handleStartOver}
                    className="btn-secondary text-lg px-8 py-4 rounded-full font-display"
                  >
                    Start Over
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={handleStartQuiz}
                className="btn-primary text-xl px-12 py-4 rounded-full font-display transform hover:scale-105 shadow-lg"
              >
                Begin Assessment
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default DoshaQuiz;
